import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Target, Loader2, Check, Sparkles, LogIn } from "lucide-react";
import { Button } from "../ui/button";
import { api, formatApiErrorDetail } from "../../lib/game";
import { useAuth } from "../../context/AuthContext";

export const CommitmentCapture = ({ sessionId, onSignIn }) => {
  const { user } = useAuth();
  const loggedIn = user && typeof user === "object";
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let active = true;
    api
      .post(`/sessions/${sessionId}/commitment-suggestions`)
      .then(({ data }) => active && setSuggestions(data.suggestions || []))
      .catch(() => active && setSuggestions([]))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [sessionId]);

  const pick = (s) => {
    setSelected(s);
    setText(s);
  };

  const save = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await api.post("/commitments", {
        session_id: sessionId,
        text: text.trim(),
        source: selected && selected === text.trim() ? "ai" : "custom",
      });
      setSaved(true);
      toast.success("Commitment saved — we'll check in with you.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (saved) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="commitment-saved"
        className="rounded-3xl border border-emerald-500/40 bg-emerald-500/[0.08] p-6 sm:p-7 text-center"
      >
        <div className="w-12 h-12 mx-auto rounded-2xl bg-emerald-500/20 border border-emerald-500/40 flex items-center justify-center mb-3">
          <Check className="w-6 h-6 text-emerald-400" />
        </div>
        <h3 className="font-heading text-xl font-bold text-slate-100">Commitment locked in</h3>
        <p className="text-sm text-slate-300 mt-1.5 max-w-md mx-auto">
          “{text.trim()}” — we'll nudge you to check in after 7, 14 and 30 days. Find it anytime under{" "}
          <span className="text-emerald-300 font-semibold">My Commitments</span>.
        </p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      data-testid="commitment-capture"
      className="rounded-3xl border border-teal-500/30 bg-gradient-to-b from-teal-500/[0.06] to-transparent bg-slate-900/70 p-6 sm:p-7"
    >
      <div className="flex items-center gap-2 mb-1">
        <Target className="w-4 h-4 text-teal-300" />
        <span className="text-xs font-semibold uppercase tracking-widest text-teal-300">
          Make a Real-Life Commitment
        </span>
      </div>
      <h3 className="font-heading text-2xl font-bold text-slate-100">Turn insight into action</h3>
      <p className="text-slate-400 text-sm mt-1.5">
        Pick a small money habit to carry into real life over the next 30 days.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm mt-5">
          <Loader2 className="w-4 h-4 animate-spin" /> Thinking of ideas based on how you played…
        </div>
      ) : (
        <div className="mt-5 space-y-2" data-testid="commitment-suggestions">
          {suggestions.map((s, i) => {
            const active = selected === s;
            return (
              <button
                key={i}
                type="button"
                data-testid={`commitment-suggestion-${i}`}
                onClick={() => pick(s)}
                className={`w-full text-left rounded-2xl border px-4 py-3 text-sm transition-all flex gap-3 items-start ${
                  active
                    ? "border-teal-400/60 bg-teal-500/10 text-slate-100"
                    : "border-slate-700 bg-slate-950/40 text-slate-300 hover:border-slate-600"
                }`}
              >
                <Sparkles className={`w-4 h-4 shrink-0 mt-0.5 ${active ? "text-teal-300" : "text-slate-500"}`} />
                <span>{s}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="mt-4">
        <label className="text-xs font-semibold uppercase tracking-wider text-slate-400">
          Or write your own
        </label>
        <textarea
          data-testid="commitment-text-input"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            setSelected(null);
          }}
          rows={2}
          maxLength={280}
          placeholder="e.g. Move a set amount into an emergency fund every payday…"
          className="mt-2 w-full rounded-2xl bg-slate-950/60 border border-slate-700 text-slate-100 text-sm px-4 py-3 placeholder:text-slate-600 focus:outline-none focus:ring-2 focus:ring-teal-500/60 resize-none"
        />
      </div>

      {loggedIn ? (
        <Button
          data-testid="save-commitment-btn"
          onClick={save}
          disabled={!text.trim() || saving}
          className="mt-4 w-full h-11 bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-400 hover:to-emerald-500 text-slate-950 font-bold rounded-xl disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save my commitment"}
        </Button>
      ) : (
        <div className="mt-4 rounded-2xl border border-slate-700 bg-slate-950/50 px-4 py-3 flex items-center gap-3">
          <p className="text-sm text-slate-300 flex-1">Sign in to save your commitment and get check-in reminders.</p>
          <Button
            data-testid="commitment-signin-btn"
            onClick={onSignIn}
            className="bg-slate-100 text-slate-900 hover:bg-white font-semibold shrink-0 h-9"
          >
            <LogIn className="w-4 h-4 mr-1.5" /> Sign In
          </Button>
        </div>
      )}
    </motion.div>
  );
};
